import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Anchor,
  Button,
  Checkbox,
  Container,
  Group,
  NumberInput,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft } from '@tabler/icons-react';
import NoNds from '@/components/common/NoNds';
import FormErrorsSummary, { type FormErrorItem } from '@/components/common/FormErrorsSummary';
import BulletinSection from '@/components/common/BulletinSection';
import PageTitle from '@/components/common/PageTitle';
import FormMessage from '@/components/auth/FormMessage';
import BasisPicker from '@/components/common/BasisPicker';
import UploadZone from '@/components/app/company/UploadZone';
import AgencyGateBanner from '@/components/app/sell/AgencyGateBanner';
import SellAttestationGate from '@/components/app/sell/SellAttestationGate';
import SellPriceOrientation from '@/components/app/sell/SellPriceOrientation';
import type { RecommendedSellPrice } from '@/types/bestPrice';
import AgentAssignmentBlock from '@/components/app/sell/form/AgentAssignmentBlock';
import FileList from '@/components/app/sell/form/FileList';
import LoadingConditionsFields from '@/components/app/sell/form/LoadingConditionsFields';
import {
  FIELD_LABEL,
  FIELD_SECTION,
  SECTION_ORDER,
  SECTION_TITLE,
  fieldError,
  focusSellField,
  sellFieldId,
  type SellSection,
} from '@/components/app/sell/form/sellFormFields';
import {
  PRICE_MIN,
  PRICE_RANGE,
  WEIGHT_MIN,
  WEIGHT_RANGE,
  COMMENT_MAX,
  DECLARATION_NO_MAX,
  buildSaleDraft,
  candidateDuplicateKey,
  exchangeLotError,
  initialSellForm,
  loadingDateErrors,
  offerDuplicateKey,
  offerProblems,
  parentGoodsOf,
  signatureOf,
  stateFromOffer,
  type SellAddress,
  type SellField,
  type SellFormState,
} from '@/components/app/sell/form/sellFormState';
import classes from './SellOfferFormPage.module.css';
import { sellService } from '@/api/services/SellService';
import { orderService } from '@/api/services/OrderService';
import { bestPriceService } from '@/api/services/BestPriceService';
import {
  SALE_TREE_QUERY_KEY,
  useAgencyAgreementMissed,
  useExchangeConstraints,
  useExchangeOrientation,
  useExchangeSession,
  useSaleOffer,
  useSaleTree,
} from '@/hooks/useSell';
import ExchangeOrientationCard from '@/components/app/sell/ExchangeOrientationCard';
import { formatMoney, formatPropertyCondition } from '@/lib/format';
import { todayIso } from '@/lib/date';
import { flattenSaleTree, groupSellByBasis } from '@/lib/sellTree';
import { getApiMessage, getStatus, isServiceUnavailable } from '@/api/errors';
import { useAuth } from '@/stores/authContext';
import { useAttestationRequired } from '@/hooks/useAttestationRequired';
import { useIsAgent } from '@/hooks/useRoles';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { ROUTES } from '@/routes/paths';

// Форма создания и правки предложения о продаже («Бюллетень»): разделы с заголовками без рамок, в правке неизменяемое
// (точка погрузки, культура, качество, год) — строкой фактов. Кнопка отправки всегда доступна: попытка с незаполненным
// показывает сводку со ссылками к полям и переводит фокус на первое из них. Одна культура и один лот, условия погрузки,
// обязательное поручение агенту.
export default function SellOfferFormPage() {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();

  // Правка — только по корректному номеру. «orderId=abc» или пустой номер раньше молча открывали форму создания
  // или правку предложения 0; теперь это «предложение не найдено».
  const editIdRaw = params.get('orderId');
  const editId = editIdRaw != null && /^[1-9]\d*$/.test(editIdRaw) ? Number(editIdRaw) : null;
  const badEditId = editIdRaw != null && editId == null;
  const editMode = editId != null;

  const agencyMissed = useAgencyAgreementMissed();
  const attestationRequired = useAttestationRequired();
  const isAgent = useIsAgent();

  const [state, setState] = useState<SellFormState>(initialSellForm);
  const [address, setAddress] = useState<SellAddress | null>(null);
  const [agentFileId, setAgentFileId] = useState<number | null>(null);
  const [agentSigned, setAgentSigned] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  // Была ли попытка отправки: до неё пустые обязательные поля не отмечаются красным.
  const [attempted, setAttempted] = useState(false);
  // В правке удалённые файлы снимаются с сервера только после успешного сохранения: уход без сохранения
  // не должен оставить опубликованное предложение без декларации.
  const [removedFileIds, setRemovedFileIds] = useState<number[]>([]);
  const submitErrorRef = useRef<HTMLDivElement>(null);
  const dirtyRef = useRef(false);
  // Адрес, для которого уже запущен упреждающий прогрев расстояний — чтобы не дёргать прогрев повторно.
  const prewarmedAddressRef = useRef<string | null>(null);

  const update = useCallback((fn: (s: SellFormState) => SellFormState) => {
    dirtyRef.current = true;
    setState(fn);
  }, []);
  const set = useCallback(
    (patch: Partial<SellFormState>) => update((s) => ({ ...s, ...patch })),
    [update],
  );

  // ── Редактирование: префилл из существующего предложения ────────────────────
  // Форму рендерим ТОЛЬКО после префилла (edit), иначе блок поручения смонтируется с пустой
  // подписью и при заполнении полей ложно перейдёт в «данные изменились», сбросив подпись.
  const offerQuery = useSaleOffer(editId);
  const [prefillDone, setPrefillDone] = useState(!editMode);
  useEffect(() => {
    if (!editMode || prefillDone || !offerQuery.data) {
      return;
    }
    const offer = offerQuery.data;
    setState(stateFromOffer(offer));
    setAddress({ address: offer.address ?? '', title: offer.addressTitle, basisId: offer.basisId });
    if (offer.agentAssignmentFile?.id != null) {
      setAgentFileId(offer.agentAssignmentFile.id);
      setAgentSigned(true);
    }
    dirtyRef.current = false;
    setPrefillDone(true);
  }, [editMode, prefillDone, offerQuery.data]);

  // ── Справочники ─────────────────────────────────────────────────────────────
  const goodsClasses = useQuery({
    queryKey: ['goodsClasses'] as const,
    queryFn: ({ signal }) => orderService.getGoodsClasses(signal),
    enabled: isAuthenticated && !agencyMissed && !attestationRequired,
    staleTime: 10 * 60_000,
  });
  const propertySets = useQuery({
    queryKey: ['goodsPropertySets', state.goodsType] as const,
    queryFn: ({ signal }) => orderService.getGoodsPropertySets(state.goodsType!, signal),
    enabled: isAuthenticated && !!state.goodsType,
    staleTime: 10 * 60_000,
  });
  const prepaymentTypes = useQuery({
    queryKey: ['sellPrepaymentTypes'] as const,
    queryFn: ({ signal }) => sellService.getPrepaymentTypes(signal),
    enabled: isAuthenticated && !agencyMissed && !attestationRequired,
    staleTime: 10 * 60_000,
  });
  const shipmentModes = useQuery({
    queryKey: ['shipmentModes'] as const,
    queryFn: ({ signal }) => sellService.getShipmentModes(signal),
    enabled: isAuthenticated && !agencyMissed && !attestationRequired,
    staleTime: 10 * 60_000,
  });
  // Биржа: ограничения (культура/качество/объём), торговое окно и ориентир цены (в правке).
  const { data: exchangeConstraints } = useExchangeConstraints();
  const { data: exchangeSession } = useExchangeSession();
  const { data: exchangeOrientation } = useExchangeOrientation(editId, editMode && state.toExchange);
  const allowedGoods = exchangeConstraints?.goodsTypes ?? [];
  const allowedSetId = exchangeConstraints?.propertiesSetIds?.[0];
  const lotError = exchangeLotError(state, exchangeConstraints ?? null);

  // При включении «биржевого» приводим к допустимым значениям: культура/качество (Пшеница 4 кл./12,5%) и
  // предоплата «без предоплаты» — их бэкенд и так требует. При выключении просто снимаем флаг.
  const onToExchangeToggle = useCallback(
    (checked: boolean) => {
      if (!checked) {
        set({ toExchange: false });
        return;
      }
      const patch: Partial<SellFormState> = { toExchange: true, prepayment: 'PERCENT_0' };
      // В правке культура/качество неизменны — не трогаем; в создании приводим к допустимым.
      if (!editMode && allowedGoods.length > 0 && (!state.goodsType || !allowedGoods.includes(state.goodsType))) {
        patch.goodsType = allowedGoods[0];
        patch.propsSetId = null;
      }
      set(patch);
    },
    [set, editMode, allowedGoods, state.goodsType],
  );

  // Рассинхрон биржевого выбора: культура или набор качества не входят в допустимые. В правке это не
  // исправить (культура/качество неизменны), поэтому и тумблер блокируем, и submit подстраховываем.
  const exchangeSelectionMismatch =
    state.toExchange &&
    exchangeConstraints != null &&
    ((!!state.goodsType && !allowedGoods.includes(state.goodsType)) ||
      (allowedSetId != null && state.propsSetId != null && state.propsSetId !== String(allowedSetId)));
  // В правке нельзя сделать биржевым оффер недопустимой культуры/качества (их в правке не изменить). Выключить биржу
  // у такого оффера можно: иначе после смены условий биржи предложение нельзя было бы сохранить никак.
  const exchangeToggleDisabled =
    editMode &&
    !state.toExchange &&
    exchangeConstraints != null &&
    ((!!state.goodsType && !allowedGoods.includes(state.goodsType)) ||
      (allowedSetId != null && state.propsSetId != null && state.propsSetId !== String(allowedSetId)));

  // Приведение культуры к допустимой в создании биржевого оффера — реактивно (в т.ч. если тумблер включили
  // до загрузки ограничений: тогда обработчик культуру не проставил, а этот эффект её выставит по приходу данных).
  useEffect(() => {
    if (!state.toExchange || editMode || allowedGoods.length === 0) {
      return;
    }
    if (!state.goodsType || !allowedGoods.includes(state.goodsType)) {
      set({ goodsType: allowedGoods[0], propsSetId: null });
    }
  }, [state.toExchange, state.goodsType, editMode, allowedGoods, set]);

  // Автовыбор допустимого набора качества (протеин 12,5%), как только он доступен для выбранной культуры.
  useEffect(() => {
    if (!state.toExchange || editMode || allowedSetId == null || !propertySets.data) {
      return;
    }
    const allowed = String(allowedSetId);
    if (propertySets.data.some((s) => String(s.propsSetId) === allowed) && state.propsSetId !== allowed) {
      set({ propsSetId: allowed });
    }
  }, [state.toExchange, state.propsSetId, editMode, allowedSetId, propertySets.data, set]);
  // Для упреждающей проверки дубликата нужна родительская культура (запрос моих предложений по basis+parent+year).
  const parentGoodsTypes = useQuery({
    queryKey: ['parentGoodsTypes'] as const,
    queryFn: ({ signal }) => orderService.getParentGoodsTypes(signal),
    enabled: isAuthenticated && !agencyMissed && !attestationRequired,
    staleTime: 10 * 60_000,
  });
  // «Мои базисы» — все мои точки с существующими предложениями (из дерева продаж), без фильтра по
  // культуре: на знакомом базисе можно разместить и новую культуру. basisId берём из дерева.
  const saleTree = useSaleTree();
  const ownBases = useMemo(
    () =>
      groupSellByBasis(flattenSaleTree(saleTree.data)).map((base) => ({
        address: base.address,
        addressTitle: base.addressTitle,
        basisId: base.items[0]?.basisId,
      })),
    [saleTree.data],
  );

  const mainPropertyName = propertySets.data?.[0]?.properties?.[0]?.name;
  const propertyData = useMemo(
    () =>
      (propertySets.data ?? [])
        .filter((s) => s.propsSetId != null && s.properties?.[0]?.value != null)
        .map((s) => {
          const p = s.properties![0]!;
          return { value: String(s.propsSetId), label: formatPropertyCondition(p) };
        }),
    [propertySets.data],
  );

  const yearOptions = useMemo(() => {
    const cy = new Date().getFullYear();
    return [cy - 1, cy, cy + 1].map((y) => String(y));
  }, []);

  // ── Файлы ────────────────────────────────────────────────────────────────────
  const onFileError = (e: unknown): void => {
    notifications.show({ color: 'red', message: getApiMessage(e) ?? t('auth.errors.generic') });
  };

  const uploadDeclaration = useMutation({
    mutationFn: (files: File[]) => sellService.uploadDeclaration(files),
    onSuccess: (uploaded) => update((s) => ({ ...s, declarationFiles: [...s.declarationFiles, ...uploaded] })),
    onError: onFileError,
  });
  const uploadSpecial = useMutation({
    mutationFn: (files: File[]) => sellService.uploadSpecialTerms(files),
    onSuccess: (uploaded) => update((s) => ({ ...s, specialTermsFiles: [...s.specialTermsFiles, ...uploaded] })),
    onError: onFileError,
  });
  const removeDeclaration = useMutation({
    mutationFn: (fileId: number) => sellService.deleteSaleFile(fileId),
    onSuccess: (_, fileId) => update((s) => ({ ...s, declarationFiles: s.declarationFiles.filter((f) => f.id !== fileId) })),
    onError: onFileError,
  });
  const removeSpecial = useMutation({
    mutationFn: (fileId: number) => sellService.deleteSaleFile(fileId),
    onSuccess: (_, fileId) => update((s) => ({ ...s, specialTermsFiles: s.specialTermsFiles.filter((f) => f.id !== fileId) })),
    onError: onFileError,
  });
  const downloadFile = useMutation({
    mutationFn: (fileId: number) => orderService.downloadOrderFile(fileId),
    onError: onFileError,
  });

  // ── Производные ────────────────────────────────────────────────────────────
  const dateErrors = useMemo(() => loadingDateErrors(state), [state]);
  const draft = useMemo(() => buildSaleDraft(state, address), [state, address]);
  const signatureKey = useMemo(() => signatureOf(state, address), [state, address]);

  const problems = useMemo(() => offerProblems(state, address), [state, address]);
  const problemMap = useMemo(() => new Map(problems.map((problem) => [problem.field, problem])), [problems]);
  const agentReady = agentSigned && agentFileId != null;

  // Всё, что мешает отправке, в порядке разделов на экране: поля, ограничения биржи, поручение и согласия. Раньше
  // ошибка биржевого лота и несовпадение с условиями биржи выключали кнопку, не попадая в сводку.
  const blockers = useMemo(() => {
    const list: { readonly label: string; readonly target: SellField | SellSection; readonly section: SellSection }[] = [];
    for (const { field } of problems) {
      const label = field === 'quality' && mainPropertyName ? mainPropertyName : t(FIELD_LABEL[field]);
      list.push({ label, target: field, section: FIELD_SECTION[field] });
    }
    if (lotError && !problemMap.has('weight')) {
      list.push({ label: t('app.sell.form.weight'), target: 'weight', section: 'goods' });
    }
    if (exchangeSelectionMismatch) {
      list.push({ label: t('app.sell.form.check.exchangeMismatch'), target: 'goods', section: 'goods' });
    }
    if (!agentReady) {
      list.push({ label: t('app.sell.form.check.agent'), target: 'agent', section: 'agent' });
    }
    if (!state.agreeSupply || !state.agreeOffer) {
      list.push({ label: t('app.sell.form.check.agree'), target: 'agree', section: 'agree' });
    }
    return list.sort((a, b) => SECTION_ORDER.indexOf(a.section) - SECTION_ORDER.indexOf(b.section));
  }, [problems, problemMap, lotError, exchangeSelectionMismatch, agentReady, state.agreeSupply, state.agreeOffer, mainPropertyName, t]);

  const canSubmit = blockers.length === 0;
  const remainingSections = [...new Set(blockers.map((blocker) => blocker.section))];

  // ── Сабмит ────────────────────────────────────────────────────────────────────
  const submit = useMutation({
    mutationFn: async (): Promise<number | null> => {
      const payload = { ...draft!, agentAssignmentFileId: agentFileId! };
      if (editMode) {
        await sellService.updateOffer(editId, { ...payload, version: offerQuery.data?.version ?? 0 });
        // Удалённые в правке файлы уже не входят в предложение; снимаем их с сервера после сохранения. Сбой не мешает
        // результату: файл без предложения ничего не меняет для покупателя.
        await Promise.allSettled(removedFileIds.map((fileId) => sellService.deleteSaleFile(fileId)));
        return editId;
      }
      // Создаём предложение сразу активным (минуя модерацию/BPMN) — оно появляется в списке
      // синхронно к ответу, без ожидания Camunda.
      return sellService.createOffer({ ...payload, activateImmediately: true });
    },
    onSuccess: () => {
      dirtyRef.current = false;
      // Дерево/группа ключуются по РОДИТЕЛЬСКОЙ культуре, а draft.goodsType — класс
      // («Пшеница 3 класс»). Ведём в список (он покажет базис→родительскую культуру),
      // чтобы не попасть на пустую группу из-за несовпадения класс↔родитель.
      // Список «Продать» строится по `saleOffersByBasis`, страница предложения — по `saleOffer`: без их сброса
      // список и страница до минуты показывали бы прежние цену и объём.
      for (const queryKey of [SALE_TREE_QUERY_KEY, ['saleOffers'], ['saleOffersByBasis'], ['saleOffer'], ['bestOffers']]) {
        void queryClient.invalidateQueries({ queryKey });
      }
      notifications.show({ color: 'lime', message: t(editMode ? 'app.sell.form.updated' : 'app.sell.form.created') });
      // В список с предвыбором фильтра базис+культура (РОДИТЕЛЬСКАЯ — по ней группируется дерево).
      const parent = state.goodsType ? parentGoodsOf(state.goodsType, parentGoodsTypes.data ?? []) : undefined;
      const qs = new URLSearchParams();
      if (address?.address) {
        qs.set('address', address.address);
      }
      if (address?.title) {
        qs.set('addressTitle', address.title);
      }
      if (parent) {
        qs.set('goods', parent);
      }
      const q = qs.toString();
      navigate(q ? `${ROUTES.APP_SELL}?${q}` : ROUTES.APP_SELL);
    },
    onError: (error) => {
      const status = getStatus(error);
      if (status === 409) {
        // Бэкенд отклонил полный дубликат (тот же ключ: базис+культура+качество+год+объём+цена+предоплата+зачёт).
        setSubmitError(t('app.sell.form.duplicate'));
      } else if (status === 412) {
        // Оптимистичная блокировка (версия устарела) — order-service отдаёт 412.
        setSubmitError(t('app.sell.form.versionConflict'));
      } else if (status === 406) {
        setSubmitError(t('app.sell.form.errAddress'));
      } else {
        setSubmitError(isServiceUnavailable(error) ? t('auth.errors.unavailable') : (getApiMessage(error) ?? t('auth.errors.generic')));
      }
    },
  });

  // Сабмит: упреждающая проверка дубликата (best-effort; бэкенд — авторитетная защита 409).
  // Сравниваем ключ кандидата с моими «живыми» предложениями по этому базису+культуре; при сбое
  // запроса не блокируем (бэкенд подстрахует). Адрес мог нормализоваться на бэке — тогда фронт может
  // не поймать, но серверная проверка вернёт 409.
  const handleSubmit = async (): Promise<void> => {
    setSubmitError(null);
    if (!canSubmit) {
      // Попытка с незаполненным: отмечаем поля, показываем сводку и ведём к первому, что нужно исправить.
      setAttempted(true);
      const first = blockers[0]!.target;
      requestAnimationFrame(() => focusSellField(first));
      return;
    }
    const key = candidateDuplicateKey(state, address);
    if (key && address) {
      setChecking(true);
      try {
        const parent = parentGoodsOf(state.goodsType!, parentGoodsTypes.data ?? []);
        const existing = await sellService.getMySaleOffers({
          address: address.address,
          addressTitle: address.title ?? '',
          parentGoodsType: parent,
          year: state.cropYear,
        });
        if (existing.some((o) => o.id !== editId && offerDuplicateKey(o) === key)) {
          setSubmitError(t('app.sell.form.duplicate'));
          return;
        }
      } catch {
        // best-effort: сетевую ошибку проверки игнорируем, дубликат отсечёт бэкенд
      } finally {
        setChecking(false);
      }
    }
    submit.mutate();
  };

  // Кнопка с индикатором загрузки теряет фокус, поэтому ошибку сохранения показываем и переводим на неё фокус.
  useEffect(() => {
    if (submitError) {
      submitErrorRef.current?.focus();
    }
  }, [submitError]);

  // ── Защита от ухода с несохранёнными данными ───────────────────────────────
  useUnsavedChangesGuard(() => dirtyRef.current && !submit.isSuccess);

  // «Продать по ней» из ориентира цены: намеренно бросаем создание оффера и уходим на экран встречного
  // предложения этому покупателю (free-mode), соглашаясь на его предоплату. Лёгкий префилл — мой базис
  // (fromAddress) + объём (prefillWeightTon); культура/качество/CPT — со стороны прайса покупателя.
  const handleSellAtBest = useCallback(
    (row: RecommendedSellPrice): void => {
      if (!address?.address || row.purchaseOrderId == null) {
        return;
      }
      const parent = state.goodsType ? parentGoodsOf(state.goodsType, parentGoodsTypes.data ?? []) : undefined;
      const qs = new URLSearchParams();
      qs.set('purchaseOrderId', String(row.purchaseOrderId));
      if (row.purchaseOrderVersion != null) {
        qs.set('purchaseOrderVersion', String(row.purchaseOrderVersion));
      }
      if (parent) {
        qs.set('goods', parent);
      }
      qs.set('year', String(state.cropYear));
      qs.set('fromAddress', address.address);
      if (address.title) {
        qs.set('fromAddressTitle', address.title);
      }
      qs.set('prepayment', row.prepaymentType);
      if (typeof state.weightTon === 'number' && state.weightTon > 0) {
        qs.set('prefillWeightTon', String(state.weightTon));
      }
      // Оборудование продавца — по нему считалась цена ориентира. Передаём на экран встречной продажи,
      // чтобы серверный (авторитетный) пересчёт цены сделки учёл ровно те же параметры, что видел продавец.
      qs.set('hasScales', String(state.hasScales));
      if (state.hasScales && typeof state.scalesLength === 'number' && state.scalesLength > 0) {
        qs.set('scalesLength', String(state.scalesLength));
      }
      qs.set('trawls', String(state.trawls));
      qs.set('trailers', String(state.trailers));
      // Намеренный уход — снимаем защиту от несохранённого, иначе useBlocker перехватит навигацию.
      dirtyRef.current = false;
      navigate(`${ROUTES.APP_SELL_COUNTER}?${qs.toString()}`);
    },
    [
      address,
      state.goodsType,
      state.cropYear,
      state.weightTon,
      state.hasScales,
      state.scalesLength,
      state.trawls,
      state.trailers,
      parentGoodsTypes.data,
      navigate,
    ],
  );

  // Агент не публикует предложения о продаже (он посредник, а не сторона сделки) — на форму создания,
  // в т.ч. по прямому URL, его не пускаем: возвращаем к списку с пояснением «Агент не публикует предложения».
  if (isAgent) {
    return <Navigate to={ROUTES.APP_SELL} replace />;
  }

  // Ограничение по аттестации — создавать предложения нельзя без неё (прямой заход по URL тоже).
  // Аттестация — базовое требование, проверяем её раньше агентского договора.
  if (attestationRequired) {
    return (
      <Container size="md" py="xl">
        <Stack gap="lg">
          <BackLink label={t('app.sell.back')} />
          <SellAttestationGate />
        </Stack>
      </Container>
    );
  }

  // Ограничение договора — раздел недоступен для продажи без подписи (прямой заход по URL тоже).
  if (agencyMissed) {
    return (
      <Container size="md" py="xl">
        <Stack gap="lg">
          <BackLink label={t('app.sell.back')} />
          <AgencyGateBanner />
        </Stack>
      </Container>
    );
  }

  if (badEditId || (editMode && offerQuery.isError)) {
    // Удалённое, чужое или неверно указанное предложение — «не найдено» без повтора; сбой сети — с повтором.
    const status = getStatus(offerQuery.error);
    const notFound = badEditId || status === 404 || status === 403;
    return (
      <Container size="md" py="xl">
        <Stack gap="lg" align="flex-start">
          <BackLink label={t('app.sell.back')} />
          <PageTitle c="var(--mantine-color-text)" fz={{ base: 26, sm: 30 }}>
            {t('app.sell.form.titleEdit')}
          </PageTitle>
          <FormMessage>
            <span>
              {notFound
                ? t('app.sell.form.notFound')
                : isServiceUnavailable(offerQuery.error)
                  ? t('auth.errors.unavailable')
                  : t('auth.errors.generic')}
            </span>
            {notFound ? null : (
              <Button variant="subtle" size="compact-sm" onClick={() => offerQuery.refetch()}>
                {t('common.retry')}
              </Button>
            )}
          </FormMessage>
        </Stack>
      </Container>
    );
  }

  if (editMode && !prefillDone) {
    return (
      <Container size="md" py="xl">
        <Stack gap="md">
          <Skeleton h={28} w={240} />
          <Skeleton h={60} />
          <Skeleton h={180} />
          <Skeleton h={180} />
        </Stack>
      </Container>
    );
  }

  const fieldErr = (field: SellField, range?: Parameters<typeof fieldError>[4]): string | undefined =>
    fieldError(problemMap, field, attempted, t, range);
  // Объём и цена ниже минимума — прежние точные тексты («не может быть меньше …»), выше максимума — общий диапазон.
  const weightErr = lotError
    ? t(lotError, { lot: exchangeConstraints?.lotSizeTonnes, min: exchangeConstraints?.minTonnes, max: exchangeConstraints?.maxTonnes })
    : state.weightTon !== '' && state.weightTon < WEIGHT_MIN
      ? t('app.sell.form.weightErr')
      : fieldErr('weight', WEIGHT_RANGE);
  const priceErr = state.price !== '' && state.price < PRICE_MIN ? t('app.sell.form.priceErr') : fieldErr('price', PRICE_RANGE);
  const qualityLabel = mainPropertyName ?? t('app.sell.form.quality');
  const qualityValue = propertyData.find((option) => option.value === state.propsSetId)?.label;
  const prepaymentName = (prepaymentTypes.data ?? []).find((p) => p.type === state.prepayment)?.name;
  const total = state.price !== '' && state.weightTon !== '' ? Number(state.price) * Number(state.weightTon) : null;

  // Удаление файла: в создании — сразу (файл ещё ничей), в правке — из списка, а с сервера после сохранения.
  const removeFile = (kind: 'declarationFiles' | 'specialTermsFiles', fileId: number): void => {
    if (!editMode) {
      (kind === 'declarationFiles' ? removeDeclaration : removeSpecial).mutate(fileId);
      return;
    }
    update((s) => ({ ...s, [kind]: s[kind].filter((file) => file.id !== fileId) }));
    setRemovedFileIds((ids) => [...ids, fileId]);
  };
  const notRemoving = { isPending: false };

  // Поле объёма — одно и то же в создании (рядом с культурой) и в правке (единственное изменяемое в разделе).
  const weightInput = (
    <NumberInput
      id={sellFieldId('weight')}
      required
      label={t('app.sell.form.weight')}
      value={state.weightTon}
      onChange={(value) => set({ weightTon: value === '' ? '' : Number(value) })}
      step={state.toExchange && exchangeConstraints ? exchangeConstraints.lotSizeTonnes : undefined}
      allowNegative={false}
      decimalScale={0}
      clampBehavior="none"
      error={weightErr}
    />
  );

  const summaryItems: FormErrorItem[] = blockers.map((blocker) => ({
    label: blocker.label,
    onSelect: () => focusSellField(blocker.target),
  }));

  return (
    <Container size="md" py="xl">
      <Stack gap={40}>
        <Stack gap={6}>
          <BackLink label={t('app.sell.back')} />
          <PageTitle c="var(--mantine-color-text)" fz={{ base: 26, sm: 30 }}>
            {t(editMode ? 'app.sell.form.titleEdit' : 'app.sell.form.titleNew')}
          </PageTitle>
          {editMode ? (
            // Неизменяемое после создания — читаемыми фактами, а не бледными заблокированными полями.
            <dl className={classes.facts}>
              <div>
                <dt>{t('app.sell.form.address')}</dt>
                <dd>
                  {address?.title || address?.address}
                  {address?.title && address.address ? <span>{address.address}</span> : null}
                </dd>
              </div>
              <div>
                <dt>{t('app.sell.form.goods')}</dt>
                <dd>{state.goodsType ?? '—'}</dd>
              </div>
              <div>
                <dt>{qualityLabel}</dt>
                <dd>{qualityValue ?? '—'}</dd>
              </div>
              <div>
                <dt>{t('app.sell.form.year')}</dt>
                <dd>{state.cropYear}</dd>
              </div>
            </dl>
          ) : (
            <Text c="gray.7" size="sm">
              {t('app.sell.form.immutableNote')}
            </Text>
          )}
        </Stack>

        {editMode ? null : (
          <BulletinSection title={t(SECTION_TITLE.where)} spaced>
            <div id={sellFieldId('address')}>
              <BasisPicker
                required
                label={t('app.sell.form.address')}
                placeholder={t('app.sell.form.addressPlaceholder')}
                description={t('app.sell.form.addressHint')}
                error={fieldErr('address')}
                ownBases={ownBases}
                value={address ? { address: address.address, addressTitle: address.title ?? '', basisId: address.basisId } : null}
                onChange={(picked) => {
                  dirtyRef.current = true;
                  setAddress(picked ? { address: picked.address, title: picked.addressTitle || undefined, basisId: picked.basisId } : null);
                  // Упреждающий прогрев кэша расстояний для ориентира: один раз на новый адрес, без ожидания ответа. Он
                  // лишь ускоряет первый показ ориентира; непустой ответ гарантирует сам запрос ориентира.
                  if (picked?.address && picked.address !== prewarmedAddressRef.current) {
                    prewarmedAddressRef.current = picked.address;
                    void bestPriceService.prewarmSellDistances(picked.address);
                  }
                }}
              />
            </div>
          </BulletinSection>
        )}

        <BulletinSection title={t(editMode ? 'app.sell.form.sectionVolume' : SECTION_TITLE.goods)} spaced>
          <Stack gap="md">
            {editMode ? null : (
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                <Select
                  id={sellFieldId('goods')}
                  required
                  label={t('app.sell.form.goods')}
                  placeholder={t('app.sell.form.goodsPlaceholder')}
                  // Биржевое предложение — только допустимая культура (НТБ): показываем и разрешаем лишь её.
                  data={state.toExchange ? (goodsClasses.data ?? []).filter((g) => allowedGoods.includes(g)) : (goodsClasses.data ?? [])}
                  value={state.goodsType}
                  onChange={(value) => set({ goodsType: value, propsSetId: null })}
                  searchable
                  disabled={state.toExchange}
                  error={fieldErr('goods') ?? (attempted && exchangeSelectionMismatch ? t('app.sell.form.check.exchangeMismatch') : undefined)}
                />
                <Select
                  id={sellFieldId('quality')}
                  required
                  label={qualityLabel}
                  placeholder={state.goodsType ? t('app.sell.form.qualityPlaceholder') : t('app.sell.form.qualityFirst')}
                  // Биржевое предложение — только допустимый набор качества (протеин 12,5 %).
                  data={state.toExchange ? propertyData.filter((o) => allowedSetId != null && o.value === String(allowedSetId)) : propertyData}
                  value={state.propsSetId}
                  onChange={(value) => set({ propsSetId: value })}
                  disabled={!state.goodsType || propertySets.isLoading || state.toExchange}
                  error={fieldErr('quality')}
                />
                <Select
                  label={t('app.sell.form.year')}
                  data={yearOptions}
                  value={String(state.cropYear)}
                  onChange={(value) => value && set({ cropYear: Number(value) })}
                  allowDeselect={false}
                />
                {weightInput}
              </SimpleGrid>
            )}
            {editMode ? (
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                {weightInput}
              </SimpleGrid>
            ) : null}
            <Switch
              // В правке культуры нет среди полей, поэтому ссылка сводки «не подходят для биржи» ведёт к тумблеру.
              id={editMode ? sellFieldId('goods') : undefined}
              checked={state.toExchange}
              onChange={(e) => onToExchangeToggle(e.currentTarget.checked)}
              label={t('app.sell.form.toExchange')}
              description={t('app.sell.form.toExchangeHint')}
              disabled={exchangeToggleDisabled}
              styles={{ description: { color: 'var(--mantine-color-gray-7)' } }}
            />
            {state.toExchange && exchangeSession ? (
              <Text size="sm" c="gray.7">
                {t('app.sell.exchange.formPublishHint', {
                  start: exchangeSession.windowStart,
                  end: exchangeSession.windowEnd,
                })}
              </Text>
            ) : null}
          </Stack>
        </BulletinSection>

        <BulletinSection title={t(SECTION_TITLE.price)} spaced>
          <Stack gap="md">
            {/* Ориентир «по какой цене можно продать»: внебиржевой — из прайсов покупателей; биржевой — лучшая
                нетто-FCA у ворот из стаканов (в правке, когда предложение уже есть), без перехода к сделке. */}
            {!state.toExchange ? (
              <SellPriceOrientation
                address={address?.address}
                goodsType={state.goodsType}
                propsSetId={state.propsSetId}
                hasScales={state.hasScales}
                hasTrawl={state.trawls}
                hasTrailer={state.trailers}
                onSell={handleSellAtBest}
              />
            ) : editMode && exchangeOrientation ? (
              <ExchangeOrientationCard orientation={exchangeOrientation} />
            ) : null}
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              <NumberInput
                id={sellFieldId('price')}
                required
                label={t('app.sell.form.price')}
                value={state.price}
                onChange={(value) => set({ price: value === '' ? '' : Number(value) })}
                allowNegative={false}
                decimalScale={2}
                thousandSeparator=" "
                clampBehavior="none"
                error={priceErr}
              />
              {state.toExchange ? (
                // Биржевое предложение всегда без предоплаты: значение текстом, а не заблокированным полем.
                <div className={classes.readonly}>
                  <span className={classes.readonlyLabel}>{t('app.sell.form.prepayment')}</span>
                  <span className={classes.readonlyValue}>{prepaymentName ?? '—'}</span>
                  <span className={classes.readonlyHint}>{t('app.sell.exchange.noPrepaymentHint')}</span>
                </div>
              ) : (
                <Select
                  id={sellFieldId('prepayment')}
                  required
                  label={t('app.sell.form.prepayment')}
                  data={(prepaymentTypes.data ?? []).map((p) => ({ value: p.type, label: p.name }))}
                  value={state.prepayment}
                  onChange={(value) => set({ prepayment: value })}
                  error={fieldErr('prepayment')}
                />
              )}
            </SimpleGrid>
            {total != null ? (
              <p className={classes.total}>
                <span>{t('app.sell.form.total')}</span> <b>{formatMoney(total)} ₽</b> <NoNds />
              </p>
            ) : null}
            <Select
              id={sellFieldId('credit')}
              required
              label={t('app.sell.form.credit')}
              data={[
                { value: 'BY_AVERAGE_VALUE', label: t('app.sell.terms.creditAverage') },
                { value: 'BY_EACH_DAY', label: t('app.sell.terms.creditEachDay') },
              ]}
              value={state.qualityCreditType}
              onChange={(value) => set({ qualityCreditType: value })}
              error={fieldErr('credit')}
            />
          </Stack>
        </BulletinSection>

        <BulletinSection title={t(SECTION_TITLE.logistics)} spaced>
          <LoadingConditionsFields
            state={state}
            set={set}
            shipmentModes={shipmentModes.data ?? []}
            dateErrors={dateErrors}
            problems={problemMap}
            attempted={attempted}
          />
        </BulletinSection>

        <BulletinSection title={t(SECTION_TITLE.declaration)} spaced>
          <Stack gap="md">
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              <TextInput
                id={sellFieldId('declarationNo')}
                required
                label={t('app.sell.form.declaration.no')}
                placeholder={t('app.sell.form.declaration.noPlaceholder')}
                value={state.declarationNo}
                maxLength={DECLARATION_NO_MAX}
                onChange={(e) => set({ declarationNo: e.currentTarget.value })}
                error={fieldErr('declarationNo')}
              />
              <TextInput
                id={sellFieldId('declarationDate')}
                required
                type="date"
                label={t('app.sell.form.declaration.date')}
                value={state.declarationDate}
                max={todayIso()}
                onChange={(e) => set({ declarationDate: e.currentTarget.value })}
                error={fieldErr('declarationDate')}
              />
            </SimpleGrid>
            <div id={sellFieldId('declarationFiles')} tabIndex={-1} className={classes.target}>
              <Stack gap="sm">
                <FileList
                  files={state.declarationFiles}
                  onRemove={(id) => removeFile('declarationFiles', id)}
                  onDownload={(id) => downloadFile.mutate(id)}
                  removing={editMode ? notRemoving : removeDeclaration}
                />
                <UploadZone
                  accept=".pdf,.png,.jpg,.jpeg,.tiff,.tif,.gif"
                  multiple
                  loading={uploadDeclaration.isPending}
                  hint={t('app.sell.form.declaration.filesHint')}
                  error={fieldErr('declarationFiles') ? t('app.sell.form.check.declaration') : undefined}
                  onFiles={(files) => uploadDeclaration.mutate(files)}
                />
              </Stack>
            </div>
          </Stack>
        </BulletinSection>

        <BulletinSection title={t(SECTION_TITLE.special)} spaced>
          <Stack gap="md">
            <Switch
              checked={state.extraConditions}
              onChange={(e) => set({ extraConditions: e.currentTarget.checked })}
              label={t('app.sell.form.special.toggle')}
            />
            {state.extraConditions ? (
              <div id={sellFieldId('specialFiles')} tabIndex={-1} className={classes.target}>
                <Stack gap="sm">
                  <FileList
                    files={state.specialTermsFiles}
                    onRemove={(id) => removeFile('specialTermsFiles', id)}
                    onDownload={(id) => downloadFile.mutate(id)}
                    removing={editMode ? notRemoving : removeSpecial}
                  />
                  <UploadZone
                    accept=".pdf,.png,.jpg,.jpeg"
                    multiple
                    loading={uploadSpecial.isPending}
                    hint={t('app.sell.form.special.filesHint')}
                    error={fieldErr('specialFiles') ? t('auth.validation.required') : undefined}
                    onFiles={(files) => uploadSpecial.mutate(files)}
                  />
                </Stack>
              </div>
            ) : null}
          </Stack>
        </BulletinSection>

        <BulletinSection title={t('app.sell.form.sectionComment')} spaced>
          <Textarea
            aria-label={t('app.sell.form.sectionComment')}
            placeholder={t('app.sell.form.commentPlaceholder')}
            value={state.userComment}
            maxLength={COMMENT_MAX}
            autosize
            minRows={2}
            maxRows={6}
            onChange={(e) => set({ userComment: e.currentTarget.value })}
          />
        </BulletinSection>

        <BulletinSection title={t(SECTION_TITLE.agent)} spaced>
          <div id={sellFieldId('agent')} tabIndex={-1} className={classes.target}>
            <AgentAssignmentBlock
              ready={draft != null}
              generate={() => sellService.generateAgentAssignment(draft!)}
              sign={(fileId) => sellService.signAgentAssignment(fileId)}
              intro={t('app.sell.form.agent.intro')}
              needFillFirst={t('app.sell.form.agent.needFillFirst')}
              signatureKey={signatureKey}
              fileId={agentFileId}
              signed={agentSigned}
              onChange={(fileId, signed) => {
                setAgentFileId(fileId);
                setAgentSigned(signed);
              }}
            />
          </div>
        </BulletinSection>

        <BulletinSection title={t(SECTION_TITLE.agree)} spaced>
          <Stack gap="sm" id={sellFieldId('agree')} tabIndex={-1} className={classes.target}>
            <Checkbox
              checked={state.agreeSupply}
              onChange={(e) => set({ agreeSupply: e.currentTarget.checked })}
              label={t('app.sell.form.agree.supply')}
              error={attempted && !state.agreeSupply ? t('auth.validation.acceptRequired') : undefined}
            />
            <Checkbox
              checked={state.agreeOffer}
              onChange={(e) => set({ agreeOffer: e.currentTarget.checked })}
              label={t('app.sell.form.agree.offer')}
              error={attempted && !state.agreeOffer ? t('auth.validation.acceptRequired') : undefined}
            />
          </Stack>
        </BulletinSection>

        <Stack gap="md">
          {submitError ? <FormMessage ref={submitErrorRef}>{submitError}</FormMessage> : null}

          {/* До попытки — спокойная строка «Осталось заполнить», после неё — сводка со ссылками к полям. Сводку не
              объявляем: фокус уже на первом поле с ошибкой, а объявление повторялось бы при каждой правке. */}
          {attempted ? (
            <FormErrorsSummary title={t('common.fixFormErrors')} items={summaryItems} />
          ) : remainingSections.length > 0 ? (
            <Text size="sm" c="gray.7">
              {t('app.sell.form.remaining')} {remainingSections.map((section) => t(SECTION_TITLE[section])).join(' · ')}
            </Text>
          ) : null}

          <Group justify="flex-end">
            <Button size="md" disabled={checking || submit.isPending} loading={checking || submit.isPending} onClick={handleSubmit}>
              {t(editMode ? 'app.sell.form.saveEdit' : 'app.sell.form.save')}
            </Button>
          </Group>
        </Stack>
      </Stack>
    </Container>
  );
}

function BackLink({ label }: { readonly label: string }) {
  return (
    <Anchor component={Link} to={ROUTES.APP_SELL} size="sm" c="brand.7" w="fit-content">
      <Group gap={4} wrap="nowrap">
        <IconArrowLeft size={16} aria-hidden />
        {label}
      </Group>
    </Anchor>
  );
}

